import Axios from "axios";
import { navigate } from "wouter/use-browser-location";
import { normalizeReturnTo } from "@/features/auth/utils/auth-state";

/*
 * A shared global instance of the API client.
 */
export const api = Axios.create({
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/*
 * An Axios interceptor to trap any 401 errors before redirecting back to
 * the auth page.
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const returnTo = normalizeReturnTo(window.location.pathname + window.location.search);
      const params = new URLSearchParams();

      if (returnTo) {
        params.set("returnTo", returnTo);
      }

      navigate(`/auth/signin?${params.toString()}`, { replace: true });
    }

    return Promise.reject(error);
  },
);

/*
 * Wait for a specified period of time.  Used to introduce a delay between
 * attempts to poll an API for status updates.
 */
const wait = (interval: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, interval);
  });
};

/*
 * Wraps an Axios method to provide a simple polling mechanism.
 */
interface PollingResult {
  status: string;
  [key: string]: any;
}

type PollingMethod = () => Promise<PollingResult>;

export const poll = async (method: PollingMethod, interval: number = 3000): Promise<PollingResult> => {
  let result: PollingResult = await method();

  while (result.status === "RUNNING") {
    await wait(interval);
    result = await method();
  }

  return result;
};
